import {
  Component,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnInit,
  OnDestroy,
  QueryList,
  ViewChildren,
  Input,
} from '@angular/core';
import { GridStack } from 'gridstack';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { ChartMetadataExtended, DataArrays } from '../../models/charts';
import { combineLatest, distinctUntilChanged, map, Subject, takeUntil } from 'rxjs';
import { ChartDataService } from '../../services/chart-data.service';
import { ChartPreviewComponent } from '../chart-preview/chart-preview.component';
import { DashboardWidget, WidgetCoordinates } from '../../models/dashboard';
import { ApiService } from '../../../shared/services/api.service';
import {
  CreateDashboardMutation,
  CreateDashboardMutationVariables,
  DashboardMetadata,
  EditDashboardMutation,
  EditDashboardMutationVariables,
  Engagements,
  GetPreSignedUrlQuery,
  GetPreSignedUrlQueryVariables,
  PreSignedUrl,
  PreSignedUrlResponse,
} from '../../../../API';
import { CreateDashboard, EditDashboard } from '../../../../graphql/mutations';
import { v4 as uuidv4 } from 'uuid';
import { AppService } from '../../../shared/services/app.service';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { FormGroup } from '@angular/forms';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Router } from '@angular/router';
import { GetPreSignedUrl } from '../../../../graphql/queries';
import { S3StorageService } from '../../../shared/services/s3-storage.service';

@Component({
  selector: 'app-dashboard-preview',
  templateUrl: './dashboard-preview.component.html',
  styleUrl: './dashboard-preview.component.scss',
})
export class DashboardPreviewComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('gridContainer', { static: false }) gridContainer!: ElementRef;
  @ViewChildren(ChartPreviewComponent) chartPreviewComponent?: QueryList<ChartPreviewComponent>;
  @ViewChildren('widgetContent', { read: ElementRef }) widgetElements!: QueryList<ElementRef>;

  @Input() dashboardDetails?: FormGroup;
  @Input() activeDashboardData?: DashboardMetadata;
  @Input() isEditMode?: boolean = false;

  private destroy$ = new Subject<void>();

  grid!: GridStack;
  widgets: DashboardWidget[] = [];

  engagement: string = '';
  isLoading: boolean = false;
  spinnerContent: string = '';

  constructor(
    private chartDataService: ChartDataService,
    private apiService: ApiService,
    private appService: AppService,
    private userManagementService: UserManagementService,
    private nzMessageService: NzMessageService,
    private router: Router,
    private s3Service: S3StorageService,
  ) {}

  ngOnInit(): void {
    combineLatest([this.chartDataService.dashboardCharts$, this.chartDataService.dashboardChartData$])
      .pipe(
        map(([selectedCharts, allChartsResultData]) => ({
          selectedCharts,
          allChartsResultData,
        })),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        takeUntil(this.destroy$),
      )
      .subscribe(({ selectedCharts, allChartsResultData }) => {
        if (this.isEditMode) {
          this.isLoading = true;
          this.spinnerContent = 'Loading data...';
        }
        if (selectedCharts.length > 0) {
          const newChart = selectedCharts[selectedCharts.length - 1];
          const chartData = allChartsResultData[newChart.chartId];

          const isChartAlreadyExist = this.activeDashboardData?.chartIds?.some((id) => id === newChart.chartId);

          if (this.isEditMode && isChartAlreadyExist) {
            const layout = this.activeDashboardData?.layout || '[]';
            const parsedLayout = JSON.parse(layout);
            parsedLayout.forEach((widget: DashboardWidget) => {
              const { x, y, w, h, id } = widget;
              if (newChart.chartId === id) {
                const existingWidgetCoordinates = {
                  x,
                  y,
                  w,
                  h,
                  id,
                };

                this.addWidget(newChart, chartData, existingWidgetCoordinates);
              }
            });
          } else {
            this.addWidget(newChart, chartData);
          }
        }
      });

    this.appService.activeEngagement$.pipe(takeUntil(this.destroy$)).subscribe((engagement: Engagements) => {
      this.engagement = engagement.engagement;
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    this.grid = GridStack.init({ float: true, column: 24 });
  }

  async saveDashboardPdf(presignedUrl: string) {
    const dashboard = this.gridContainer.nativeElement;
    const elementWidth = dashboard.offsetWidth + 20;
    const elementHeight = dashboard.offsetHeight + 20;
    const canvas = await html2canvas(dashboard, {
      scale: 2,
      height: elementHeight,
      width: elementWidth,
    });

    const imgData = canvas.toDataURL('image/png');
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    // Convert px to mm (assuming 96 DPI)
    const pdfWidthMm = canvasWidth / 3.7795275591;
    const pdfHeightMm = canvasHeight / 3.7795275591;

    const pdf = new jsPDF({
      orientation: pdfWidthMm > pdfHeightMm ? 'l' : 'p',
      unit: 'mm',
      format: [pdfWidthMm, pdfHeightMm],
    });

    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidthMm, pdfHeightMm);

    const blob = pdf.output('blob');

    await this.s3Service.uploadPdf(presignedUrl, blob);
  }

  exportAsPng() {
    const dashboard = this.gridContainer.nativeElement;

    html2canvas(dashboard).then((canvas) => {
      const link = document.createElement('a');
      link.download = 'dashboard.png';
      link.href = canvas.toDataURL();
      link.click();
    });
  }

  addWidget(chart: ChartMetadataExtended, chartData: DataArrays, existingWidget?: WidgetCoordinates) {
    const newWidget: DashboardWidget = {
      id: existingWidget ? existingWidget.id : chart.chartId,
      chartId: chart.chartId,
      x: existingWidget ? existingWidget.x : 0,
      y: existingWidget ? existingWidget.y : 0,
      w: existingWidget ? existingWidget.w : 12,
      h: existingWidget ? existingWidget.h : 6,
      chartType: chart.chartType,
      chartData: chartData,
      chartConfig: JSON.parse(chart?.config || '{}'),
    };

    const isWidgetAlreadyExist = this.widgets.some((item) => item.chartId === chart.chartId);

    if (!isWidgetAlreadyExist) {
      this.widgets.push(newWidget);
    }

    // Let Angular render first
    setTimeout(() => {
      const el = this.gridContainer.nativeElement.querySelector(`[gs-id="${chart.chartId}"]`);

      if (el) {
        this.grid.makeWidget(el); // 👈 this is what enables dragging/resizing
      }
    }, 500);

    this.isLoading = false;
    this.spinnerContent = '';
  }

  removeWidget(widgetId: string): void {
    //update selected charts
    const existingSelectedCharts = this.chartDataService.selectedChartsForDashboard.value;
    const filteredCharts = existingSelectedCharts.filter((item) => item.chartId !== widgetId);
    this.chartDataService.selectedChartsForDashboard.next(filteredCharts);

    // update widget list
    const widgetEl = this.gridContainer.nativeElement.querySelector(`.grid-stack-item[gs-id="${widgetId}"]`);
    if (widgetEl) {
      this.grid.removeWidget(widgetEl);
    }
    const remainingWidgets = this.widgets.filter((w) => w.chartId !== widgetId);
    this.widgets = [...remainingWidgets];

    const remainingActiveWidgets = this.activeDashboardData?.chartIds?.filter(
      (existingWidget) => existingWidget !== widgetId,
    ) as string[];

    if (this.activeDashboardData) {
      this.activeDashboardData.chartIds = [...remainingActiveWidgets];
    }

    if (this.widgets.length === 0) {
      this.isLoading = false;
      this.spinnerContent = '';
    }
  }

  async saveDashboardDetails(dashboardId: string) {
    const layout = this.grid.save(false);
    const { name = '', description = '' } = this.dashboardDetails?.value || {};

    await this.apiService.query<CreateDashboardMutation, CreateDashboardMutationVariables>(CreateDashboard, {
      input: {
        dashboardId,
        chartIds: this.widgets.map((widget) => widget.chartId),
        engagement: this.engagement,
        orgType: this.userManagementService.getOrgType(),
        createdBy: this.userManagementService.getEmail(),
        lastModifiedBy: this.userManagementService.getEmail(),
        layout: JSON.stringify(layout),
        name,
        description,
      },
    });
  }

  async getPresignedUrlForImage(dashboardId: string): Promise<PreSignedUrlResponse | void> {
    const input: PreSignedUrl = {
      contentType: 'image/png',
      bucketSuffix: 'bi-image-storage',
      s3keyName: `images/dashboard/${this.engagement}/${dashboardId}/dashboard.png`,
    };

    const response = await this.apiService.query<GetPreSignedUrlQuery, GetPreSignedUrlQueryVariables>(GetPreSignedUrl, {
      input,
    });

    const preSigned = response.data?.GetPreSignedUrl;

    if (!preSigned?.putUrl || !preSigned?.deleteUrl || preSigned.statusCode !== 200) {
      this.nzMessageService.error('Failed to get pre-signed URL, please try again');
      return;
    }

    return preSigned;
  }

  async getPresignedUrlForPdf(dashboardId: string): Promise<PreSignedUrlResponse | void> {
    const input: PreSignedUrl = {
      contentType: 'application/pdf',
      bucketSuffix: 'bi-image-storage',
      s3keyName: `images/dashboard/${this.engagement}/${dashboardId}/dashboard.pdf`,
    };

    const response = await this.apiService.query<GetPreSignedUrlQuery, GetPreSignedUrlQueryVariables>(GetPreSignedUrl, {
      input,
    });

    const preSigned = response.data?.GetPreSignedUrl;

    if (!preSigned?.putUrl || !preSigned?.deleteUrl || preSigned.statusCode !== 200) {
      this.nzMessageService.error('Failed to get pre-signed URL, please try again');
      return;
    }

    return preSigned;
  }

  async saveDashboardImage(presignedUrl: string) {
    const dashboard = this.gridContainer.nativeElement;
    const elementWidth = dashboard.offsetWidth + 20;
    const elementHeight = dashboard.offsetHeight + 20;

    const canvas = await html2canvas(dashboard, {
      scale: 2,
      height: elementHeight,
      width: elementWidth,
    });

    const blob: Blob = await new Promise((resolve) => canvas.toBlob((b) => resolve(b!), 'image/png'));

    await this.s3Service.uploadPng(presignedUrl, blob);
  }

  async saveDashboard() {
    const dashboardId = uuidv4();

    this.isLoading = true;
    this.spinnerContent = 'Saving dashboard details...';
    const removeButtonCollection = document.getElementsByClassName('remove-widget-btn');
    [...removeButtonCollection].forEach((element) => {
      (element as HTMLElement).style.display = 'none';
    });

    // Step 1
    const preSignedImage = await this.getPresignedUrlForImage(dashboardId);

    // Step 2
    const preSignedPdf = await this.getPresignedUrlForPdf(dashboardId);

    try {
      // Step 3
      preSignedImage?.putUrl && (await this.saveDashboardImage(preSignedImage?.putUrl));

      // Step 4
      preSignedPdf?.putUrl && (await this.saveDashboardPdf(preSignedPdf?.putUrl));

      // Step 5
      await this.saveDashboardDetails(dashboardId);

      // Success
      this.nzMessageService.success('Dashboard saved successfully');
      this.router.navigate(['/bi/manage-dashboards']);
    } catch (error: any) {
      // Cleanup only if Step 4 failed after image & pdf upload
      if (preSignedImage?.deleteUrl && preSignedPdf?.deleteUrl) {
        try {
          await this.s3Service.deletePresignedChartAndDashboard(preSignedImage.deleteUrl);
          await this.s3Service.deletePresignedChartAndDashboard(preSignedPdf.deleteUrl);
        } catch {
          console.warn('Image cleanup failed');
        }
      }

      this.nzMessageService.error(`Failed to save dashboard due to error ${error?.message || error} `);
    } finally {
      this.isLoading = false;
      this.spinnerContent = '';
      const removeButtonCollection = document.getElementsByClassName('remove-widget-btn');
      [...removeButtonCollection].forEach((element) => {
        (element as HTMLElement).style.display = 'block';
      });
    }
  }

  async updateDashboardDetails(dashboardId: string) {
    const layout = this.grid.save(false);
    const { name = '', description = '' } = this.dashboardDetails?.value || {};

    await this.apiService.query<EditDashboardMutation, EditDashboardMutationVariables>(EditDashboard, {
      input: {
        dashboardId,
        chartIds: this.widgets.map((widget) => widget.chartId),
        engagement: this.engagement,
        orgType: this.userManagementService.getOrgType(),
        lastModifiedBy: this.userManagementService.getEmail(),
        layout: JSON.stringify(layout),
        name,
        description,
      },
    });
  }

  async updateDashboard(dashboardId: string) {
    this.isLoading = true;
    this.spinnerContent = 'Updating dashboard details...';
    const removeButtonCollection = document.getElementsByClassName('remove-widget-btn');
    [...removeButtonCollection].forEach((element) => {
      (element as HTMLElement).style.display = 'none';
    });

    // Step 1
    const preSignedImage = await this.getPresignedUrlForImage(dashboardId);

    // Step 2
    const preSignedPdf = await this.getPresignedUrlForPdf(dashboardId);

    try {
      // Step 3
      preSignedImage?.putUrl && (await this.saveDashboardImage(preSignedImage?.putUrl));

      // Step 4
      preSignedPdf?.putUrl && (await this.saveDashboardPdf(preSignedPdf?.putUrl));

      // Step 5
      await this.updateDashboardDetails(dashboardId);

      // Success
      this.nzMessageService.success('Dashboard updated successfully');
      this.router.navigate(['/bi/manage-dashboards']);
    } catch (error: any) {
      // Cleanup only if Step 4 failed after image & pdf upload
      if (preSignedImage?.deleteUrl && preSignedPdf?.deleteUrl) {
        try {
          await this.s3Service.deletePresignedChartAndDashboard(preSignedImage.deleteUrl);
          await this.s3Service.deletePresignedChartAndDashboard(preSignedPdf.deleteUrl);
        } catch {
          console.warn('Image cleanup failed');
        }
      }

      this.nzMessageService.error(`Failed to update dashboard due to error ${error?.message || error} `);
    } finally {
      this.isLoading = false;
      this.spinnerContent = '';
      const removeButtonCollection = document.getElementsByClassName('remove-widget-btn');
      [...removeButtonCollection].forEach((element) => {
        (element as HTMLElement).style.display = 'block';
      });
    }
  }
}
